import React, {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useDispatch} from 'react-redux'
import {setSelectedEngineTypes} from '../../../../features/general/generalSlice'
import Option from '../../../../utils/filters/Option'
import getNewOptionValue from '../../../../utils/filters/getNewOptionValue'
import MultiSelectStatus from '../../ui/MultiSelectStatus'
import {Params} from '../../../../shared/variables'
import {useFeatureServices} from '../../../../use-feature-services'
import {setUrlParams} from '../../../../utils/url/setUrlParams'
import ResponsiveMultiSelect from '../../ui/ResponsiveMultiSelect'

export interface FilterByCarEngineProps {
    engineOptions: Option
    selectedEngines: string[]
    onClick: () => void
}

export default function FilterByCarEngine(
    props: FilterByCarEngineProps
): React.JSX.Element {
    const {t} = useTranslation('app')
    const dispatch = useDispatch()
    const {'s2:history': historyService} = useFeatureServices() as any
    const [multiselectStatusMessage, setMultiselectStatusMessage] =
        useState<string>(
            t('filters.noneSelected', {
                title: t('filters.aria-labels.engineType'),
            })
        )

    const updateStatusFromValues = (values: string[]) => {
        const selectedLabels = values.map(
            // eslint-disable-next-line security/detect-object-injection
            (key) => props.engineOptions[key] || key
        )

        setMultiselectStatusMessage(
            selectedLabels.length > 0
                ? t('filters.statusMessage', {
                      title: t('filters.aria-labels.engineType'),
                      values: selectedLabels.join(', '),
                  })
                : t('filters.noneSelected', {
                      title: t('filters.aria-labels.engineType'),
                  })
        )
    }

    const appliedFromUrlRef = useRef(false)
    useEffect(() => {
        if (appliedFromUrlRef.current) {
            return
        }
        if (!props.engineOptions || !Object.keys(props.engineOptions).length) {
            return
        }

        const url = new URL(window.location.href)
        const raw =
            url.searchParams.get(Params.FUELS) ??
            url.searchParams.get(`${Params.FUELS}-app`) ??
            ''

        if (!raw) {
            appliedFromUrlRef.current = true

            return
        }

        const urlValues = raw
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)

        const validValues = urlValues.filter((v) =>
            Object.prototype.hasOwnProperty.call(props.engineOptions, v)
        )

        if (validValues.length) {
            dispatch(setSelectedEngineTypes(validValues))
            updateStatusFromValues(validValues)
        }

        appliedFromUrlRef.current = true
    }, [props.engineOptions, dispatch])

    const handleOptionClick = (value: string) => {
        props.onClick()

        const newValue = getNewOptionValue(props.selectedEngines, value)
        dispatch(setSelectedEngineTypes(newValue))
        updateStatusFromValues(newValue)

        setUrlParams(
            historyService,
            Params.FUELS,
            newValue.length ? newValue.join(',') : ''
        )
    }

    return (
        <>
            <ResponsiveMultiSelect
                name="engineType"
                label={t('filters.engineType')}
                value={props.selectedEngines}
                options={props.engineOptions}
                onOptionClick={handleOptionClick}
            />
            {multiselectStatusMessage && (
                <MultiSelectStatus message={multiselectStatusMessage} />
            )}
        </>
    )
}
